// ── backupExport.js — 셈핏 전체 데이터 1파일 백업 ────────────────
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../db';
import { findPriceByName } from './ingredientMatch';

// 서브컬렉션만 Firestore에서 fetch (단일 문서 필드는 호출자가 providedData로 제공)
async function fetchSubcollections(uid) {
  const out = {};
  const cols = ['transactions', 'purchaseLog'];
  for (const col of cols) {
    try {
      const snap = await getDocs(collection(db, 'users', uid, col));
      out[col] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) { console.warn('fetch ' + col + ' failed:', e.message); out[col] = []; }
  }
  return out;
}

const m = n => Number(n) || 0;

export function buildTransactionsSheet(txs) {
  const out = [['ID','날짜','유형','거래처','품목','공급가액','부가세','면세금액','총액','category.major','category.sub','결제수단','증빙','메모','source','receiptFingerprint']];
  for (const t of (txs || [])) {
    out.push([
      t.id, t.date || '', t.type || '', t.partnerName || '', t.itemName || '',
      m(t.supply), m(t.vat), m(t.taxFreeAmount),
      m(t.total != null ? t.total : ((Number(t.supply)||0) + (Number(t.vat)||0))),
      t.category?.major || '', t.category?.sub || '',
      t.revenueCategory || t.evidence?.type || '',
      t.evidence?.type || '', t.memo || '', t.source || '', t.receiptFingerprint || '',
    ]);
  }
  return out;
}

export function buildPricesSheet(prices, opts = {}) {
  // V112: 제조사·중국어 열은 맨 뒤에 붙인다 (복원은 열 이름으로 읽으므로 옛 백업과도 호환)
  // V127: opts.pron = { header, of(p) } 이면 발음 열을 맨 뒤에 (단가표 엑셀 다운로드용. 백업엔 안 넣음 — 복원은 이 열을 무시)
  const pron = opts.pron && typeof opts.pron.of === 'function' ? opts.pron : null;
  const head = ['코드','재료명','규격','단위','단가','용량(net_g)','카테고리','거래처','AI매칭','제조사','중국어명','제조사(中)','거래처(中)','규격(中)','단위(中)','구매 링크','로스(g)'];   // V152 신선식품 로스
  const out = [pron ? [...head, pron.header] : head];
  for (const p of (prices || [])) {
    const row = [p.code||'', p.name||'', p.spec||'', p.unit||'', m(p.price), m(p.net_g), p.category||'', p.vendor||'', p.aiCanonical||'',
      p.maker||'', p.name_zh||'', p.maker_zh||'', p.vendor_zh||'', p.spec_zh||'', p.unit_zh||'', p.link||'', p.loss_g ? m(p.loss_g) : ''];
    if (pron) row.push(pron.of(p) || '');
    out.push(row);
  }
  return out;
}

export function buildRecipesSheet(categories) {
  const out = [['카테고리','ID','이름','유형','총생산량','인분(g)','판매가','재료수','재료(이름:중량)','메모']];
  for (const c of (categories || [])) {
    for (const item of (c.items || [])) {
      const ings = (item.ingredients || []).map(i => i.name + ':' + i.qty).join('; ');
      out.push([c.name||'', item.id||'', item.name||'', item.type||'', m(item.total_output), m(item.serving_g), m(item.selling_price), (item.ingredients||[]).length, ings, item.note||'']);
    }
  }
  return out;
}

// ── 한 줄에 한 재료 — 단위·단가·원가 포함 (단가표와 조인) ──
export function buildRecipeIngredientsSheet(categories, prices) {
  const out = [['카테고리','레시피ID','레시피명','재료명','사용량','단위','단가','재료원가','단가표매칭']];
  const priceArr = Array.isArray(prices) ? prices : [];
  for (const c of (categories || [])) {
    for (const item of (c.items || [])) {
      for (const ing of (item.ingredients || [])) {
        const matched = findPriceByName(priceArr, ing?.name);
        const qty = m(ing?.qty);
        const unitPrice = matched ? m(matched.price) : 0;
        const cost = Math.round(qty * unitPrice);
        out.push([
          c.name || '',
          item.id || '',
          item.name || '',
          ing?.name || '',
          qty,
          matched?.unit || '',
          unitPrice,
          cost,
          matched ? matched.name : '(매칭 없음)',
        ]);
      }
    }
  }
  return out;
}

function buildEmployeesSheet(emps) {
  const out = [['ID','이름','직위','고용형태','시급/월급','기준시급','입사일','연락처','메모']];
  for (const e of (emps || [])) out.push([e.id, e.name||'', e.role||'', e.employmentType||'', m(e.wage), m(e.hourlyWage), e.hireDate||'', e.phone||'', e.memo||'']);
  return out;
}

function buildSchedulesSheet(schedules) {
  const out = [['월','일','직원ID','근무시간','메모']];
  for (const [mk, ms] of Object.entries(schedules || {})) {
    if (!ms || typeof ms !== 'object') continue;
    for (const [day, ds] of Object.entries(ms)) {
      if (!ds || typeof ds !== 'object') continue;
      for (const [empId, shift] of Object.entries(ds)) {
        if (!shift || (!shift.start && !shift.blocks)) continue;
        const bt = Array.isArray(shift.blocks) && shift.blocks.length > 0
          ? shift.blocks.map(b => b.start + '-' + b.end).join('; ')
          : (shift.start || '') + '-' + (shift.end || '');
        out.push([mk, day, empId, bt, shift.note || '']);
      }
    }
  }
  return out;
}

function buildProfitLossSheet(pl) {
  const out = [['월','카드(수기)','현금(수기)','배달(수기)','식재료비','인건비','임대료','공과금','기타','메모']];
  const rows = [];
  for (const [mk, rec] of Object.entries(pl || {})) {
    if (!rec || typeof rec !== 'object') continue;
    const r = rec.revenue || {}, c = rec.costs || {};
    rows.push([mk, m(r.card), m(r.cash), m(r.delivery), m(c.ingredients), m(c.labor), m(c.rent), m(c.utilities), m(c.other), rec.memo||'']);
  }
  rows.sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  return [...out, ...rows];
}

export function buildPurchaseLogSheet(logs) {
  const out = [['ID','날짜','거래처','증빙유형','공급가액','부가세','면세금액','총액','품목수','메모']];
  for (const p of (logs || [])) out.push([p.id, p.date||'', p.partnerName||'', p.evidenceType||'', m(p.supply), m(p.vat), m(p.taxFreeAmount), m(p.total), (p.items||[]).length, p.memo||'']);
  return out;
}

function buildOrderHistorySheet(orders) {
  const out = [['ID','날짜','제목','항목수','총금액','메모']];
  for (const o of (orders || [])) out.push([o.id, o.date||'', o.title||'', (o.items||[]).length, m(o.totalCost), o.memo||'']);
  return out;
}

function buildInventorySheet(inv) {
  const items = (inv && inv.items) || {};
  const out = [['재료명','현재고','안전재고','단위','메모']];
  for (const [name, val] of Object.entries(items)) {
    if (!val || typeof val !== 'object') continue;
    out.push([name, m(val.currentStock), m(val.safetyStock), val.unit || '', val.memo || '']);
  }
  return out;
}

function buildSettingsSheet(s) {
  const x = s || {};
  return [
    ['항목', '값'],
    ['매장명', x.storeName || ''],
    ['주소', x.address || ''],
    ['사업자번호', x.businessNumber || ''],
    ['업종', x.industry || ''],
    ['예상 월 인분', x.expectedMonthlyServings || ''],
    ['주휴수당 사용', x.useWeeklyHolidayPay ? 'O' : ''],
    ['4대보험 자동', x.autoInsurance ? 'O' : ''],
  ];
}

function styleHeader(sheet, cols) {
  for (let c = 0; c < cols; c++) {
    const addr = window.XLSX.utils.encode_cell({ r: 0, c });
    if (!sheet[addr]) continue;
    sheet[addr].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '1E6B3C' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
}

function autoCols(aoa) {
  return (aoa[0] || []).map((_, c) => {
    let max = String(aoa[0][c] || '').length;
    for (let r = 1; r < Math.min(aoa.length, 100); r++) {
      const len = String(aoa[r]?.[c] ?? '').length;
      if (len > max) max = len;
    }
    return { wch: Math.min(40, Math.max(8, max + 2)) };
  });
}

export function exportSingleSheet({ fileName, sheetName, aoa }) {
  if (typeof window === 'undefined' || !window.XLSX) throw new Error('엑셀 라이브러리 로드 실패');
  const XLSX = window.XLSX;
  const wb = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  if (aoa.length > 0) styleHeader(sheet, aoa[0].length);
  sheet['!cols'] = autoCols(aoa);
  XLSX.utils.book_append_sheet(wb, sheet, sheetName);
  XLSX.writeFile(wb, fileName);
}

export async function exportFullBackup(uid, providedData, onProgress) {
  if (!uid) throw new Error('로그인 필요');
  if (typeof window === 'undefined' || !window.XLSX) throw new Error('엑셀 라이브러리 로드 실패');
  const XLSX = window.XLSX;

  onProgress && onProgress('서브컬렉션 가져오는 중...');
  const subs = await fetchSubcollections(uid);

  // App.js에서 이미 복호화된 단일 문서 필드들 (categories, prices, profitLoss 등)
  const pd = providedData || {};
  const data = {
    transactions: subs.transactions || [],
    purchaseLog: subs.purchaseLog || [],
    categories: pd.categories || [],
    prices: pd.prices || [],
    profitLoss: pd.profitLoss || {},
    employees: pd.employees || [],
    schedules: pd.schedules || {},
    inventory: pd.inventory || {},
    orderHistory: pd.orderHistory || [],
    payrollSettings: pd.payrollSettings || {},
    industry: pd.industry || '',
  };

  onProgress && onProgress('엑셀 만드는 중...');
  const wb = XLSX.utils.book_new();

  const cats = Array.isArray(data.categories) ? data.categories : (data.categories?.categories || []);
  const pl = data.profitLoss || {};
  const inv = data.inventory || {};
  const ps = data.payrollSettings || {};

  const sheets = [
    { n: '거래장부', a: buildTransactionsSheet(data.transactions || []) },
    { n: '단가표', a: buildPricesSheet(data.prices || []) },
    { n: '레시피', a: buildRecipesSheet(cats) },
    { n: '레시피_재료', a: buildRecipeIngredientsSheet(cats, data.prices || []) },
    { n: '직원', a: buildEmployeesSheet(data.employees || []) },
    { n: '스케줄', a: buildSchedulesSheet(data.schedules || {}) },
    { n: '손익수기', a: buildProfitLossSheet(pl) },
    { n: '매입메모', a: buildPurchaseLogSheet(data.purchaseLog || []) },
    { n: '발주내역', a: buildOrderHistorySheet(data.orderHistory || []) },
    { n: '재고', a: buildInventorySheet(inv) },
    { n: '설정', a: buildSettingsSheet(ps) },
  ];

  for (const sh of sheets) {
    const sheet = XLSX.utils.aoa_to_sheet(sh.a);
    if (sh.a.length > 0) styleHeader(sheet, sh.a[0].length);
    sheet['!cols'] = autoCols(sh.a);
    XLSX.utils.book_append_sheet(wb, sheet, sh.n);
  }

  const recipeCount = cats.reduce((s, c) => s + (c.items || []).length, 0);
  const meta = [
    ['항목', '값'],
    ['백업 일시', new Date().toLocaleString('ko-KR')],
    ['사용자 UID', uid],
    ['거래 건수', (data.transactions || []).length],
    ['단가표 항목', (data.prices || []).length],
    ['레시피 수', recipeCount],
    ['직원 수', (data.employees || []).length],
    ['매입 메모', (data.purchaseLog || []).length],
    ['발주 내역', (data.orderHistory || []).length],
  ];
  const metaSheet = XLSX.utils.aoa_to_sheet(meta);
  styleHeader(metaSheet, 2);
  metaSheet['!cols'] = [{ wch: 20 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, metaSheet, '백업정보');

  onProgress && onProgress('다운로드 중...');
  const t = new Date();
  const fileName = '셈핏_백업_' + t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0') + '-' + String(t.getDate()).padStart(2, '0') + '.xlsx';
  XLSX.writeFile(wb, fileName);

  return {
    fileName,
    counts: {
      transactions: (data.transactions || []).length,
      prices: (data.prices || []).length,
      recipes: recipeCount,
      employees: (data.employees || []).length,
      purchaseLog: (data.purchaseLog || []).length,
      orderHistory: (data.orderHistory || []).length,
    },
  };
}
